import { useLazyFetchVesselsQuery, useAddVesselMutation } from '@api/Trinium/Endpoints/Vessels/slice';
import { Vessel, VoyageWithVesselInfo } from '@api/Trinium/Endpoints/Vessels/types';
import { FormikComplexAutocomplete } from '@forms/Components/AutocompleteInput';
import useDebouncer from '@hooks/useDebounce';
import { Field, useFormikContext } from 'formik';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/AddCircleOutlineTwoTone';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';
import DisabledTooltip from '@components/DisabledTooltip';
import useVesselVoyageModal from './useVesselVoyageModal';

import styles from './Location.module.css';

const styleContext = classNames.bind(styles);

interface VesselAutocompleteFieldProps {
	fieldName: string;
	label?: string;
	className?: string;
	disabled?: boolean;
	disabledReason?: string;
	required?: boolean;
	canAddVessel?: boolean;
	/** Companion field name for the Voyage input. When present allows the form to clear the Voyage if the Vessel changes. */
	voyageFieldName?: string;
	/**
	 * What the + button adds. 'vessel' opens the bare Add Vessel modal, which is all that is left to
	 * add once you are already inside the Vessel & Voyage modal. 'vesselVoyage' opens the combined
	 * modal, so a form that has both fields captures the vessel and its voyage in one pass.
	 */
	addAction?: 'vessel' | 'vesselVoyage';
	/** Direction of the voyage the combined modal should collect. Only read for addAction='vesselVoyage'. */
	isExport?: boolean;
	/** Runs with the voyage the combined modal saved, so the form can sync dependent fields (e.g. FRD / Cutoff) */
	onVoyageApplied?: (voyage: VoyageWithVesselInfo | null) => void;
}

export function VesselAutocompleteField(props: VesselAutocompleteFieldProps) {
	const { getFieldMeta, setFieldValue } = useFormikContext();
	const { value: selectedVessel } = getFieldMeta<Vessel | null>(props.fieldName);
	const [fetchVessels, { data: vessels, isFetching }] = useLazyFetchVesselsQuery();
	const appDispatch = useAppDispatch();

	const uniqueCacheKey = useId();
	const [, { data: newVessel }] = useAddVesselMutation({
		fixedCacheKey: uniqueCacheKey
	});

	const openVesselVoyageModal = useVesselVoyageModal({
		isExport: props.isExport ?? false,
		vesselFieldName: props.fieldName,
		voyageFieldName: props.voyageFieldName,
		onVoyageApplied: props.onVoyageApplied
	});

	const [debouncedValue, setDebouncedValue] = useState<string | null>(null);

	useEffect(() => {
		if (newVessel) {
			void setFieldValue(props.fieldName, newVessel, true);
		}
	}, [newVessel, setFieldValue, props.fieldName]);

	useDebouncer(
		() => {
			if (debouncedValue && debouncedValue.length > 1) {
				void fetchVessels({ startsWith: debouncedValue });
			}
		},
		[debouncedValue],
		500
	);

	const onKeyDown = useCallback((value: string | null) => {
		setDebouncedValue(value);
	}, []);

	const onVesselAdd = () => {
		if (props.addAction === 'vesselVoyage') {
			openVesselVoyageModal({ vessel: selectedVessel });
			return;
		}

		appDispatch(
			queueModal({
				Component: 'addVessel',
				Props: { apiFixedCacheKey: uniqueCacheKey }
			})
		);
	};

	const { onVoyageApplied, voyageFieldName } = props;

	const onVesselChange = useCallback(() => {
		if (voyageFieldName) {
			void setFieldValue(voyageFieldName, null, true);
			// Fields derived from the voyage go with it
			onVoyageApplied?.(null);
		}
	}, [onVoyageApplied, setFieldValue, voyageFieldName]);

	// `useLazyFetchVesselsQuery` returns `undefined` until the user types, so `vessels ?? []` would yield
	// a fresh array every render and cause the consumer's effect (keyed on autoCompleteComplexItems) to
	// re-fire and revert local state. Memoize to keep the reference stable while the data is empty.
	const vesselOptions = useMemo(() => vessels ?? [], [vessels]);

	return (
		<DisabledTooltip message={props.disabled ? props.disabledReason : undefined} wrapperStyle={{ flexBasis: '100%' }}>
			<div
				className={styleContext({
					['flex-container']: true,
					[props.className ?? '']: Boolean(props.className)
				})}
			>
				<Field
					name={props.fieldName}
					label={props.label ?? 'Vessel'}
					component={FormikComplexAutocomplete}
					className={styles['vessel-autocomplete']}
					autoCompleteComplexItems={vesselOptions}
					onInputChange={onKeyDown}
					getOptionKey={getVesselOptionKey}
					getOptionLabel={getVesselOptionLabel}
					isOptionEqualToValue={isVesselOptionEqual}
					disabled={props.disabled}
					required={props.required}
					isLoading={isFetching}
					onChangeExtension={onVesselChange}
				/>
				{props.canAddVessel && !props.disabled && (
					<IconButton
						onClick={onVesselAdd}
						className={styles['add-ico']}
					>
						<AddIcon color='success' />
					</IconButton>
				)}
			</div>
		</DisabledTooltip>
	);
}

const getVesselOptionKey = (option?: Vessel) => option?.id ?? null;

const getVesselOptionLabel = (option?: Vessel) => (option ? `${option.name} (${option.id})` : '');

const isVesselOptionEqual = (option?: Vessel, value?: Vessel) => option?.id === value?.id;
